import { COLORS } from '@repo/shared_utils'
import { APP_NAME } from '@repo/shared_utils/constants'
import { createFileRoute } from '@tanstack/react-router'
import { BarChart3, Clock, FileText, Shield, Users, Zap } from 'lucide-react'

export const Route = createFileRoute('/dashboard/admin/og-cards/')({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-8">
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 max-w-6xl w-full">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
          OG Card Preview
        </h1>

        {/* OG Card */}
        <div
          className="relative mx-auto bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-indigo-900 dark:to-purple-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700"
          style={{
            width: '1200px',
            height: '630px',
            maxWidth: '100%',
            aspectRatio: '1200/630',
          }}
        >
          {/* Background decorative elements */}
          <div className="absolute inset-0">
            <div
              className="absolute top-10 right-10 w-80 h-80 rounded-full opacity-10 blur-3xl"
              style={{ backgroundColor: COLORS[1] }}
            />
            <div
              className="absolute bottom-10 left-10 w-80 h-80 rounded-full opacity-10 blur-3xl"
              style={{ backgroundColor: COLORS[2] }}
            />
            <div
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-5 blur-3xl"
              style={{ backgroundColor: COLORS[0] }}
            />
          </div>

          {/* Grid pattern overlay */}
          <div className="absolute inset-0 opacity-[0.02]">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, ${COLORS[1]} 1px, transparent 0)`,
                backgroundSize: '40px 40px',
              }}
            />
          </div>

          {/* Main content */}
          <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-16">
            {/* App name */}
            <div className="mb-8">
              <h1 className="text-8xl font-bold mb-4">
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 bg-clip-text text-transparent">
                  {APP_NAME}
                </span>
              </h1>
              <p className="text-3xl text-gray-600 dark:text-gray-300 font-medium">
                Modern Payroll Management
              </p>
            </div>

            {/* Value proposition */}
            <div className="mb-12">
              <p className="text-2xl text-gray-700 dark:text-gray-200 max-w-4xl leading-relaxed">
                Streamline your workforce management with our intuitive platform designed for
                businesses of all sizes
              </p>
            </div>

            {/* Feature icons */}
            <div className="flex justify-center items-center gap-8 mb-8">
              {[
                { icon: Users, color: COLORS[0], label: 'Employee Management' },
                { icon: Clock, color: COLORS[1], label: 'Time Tracking' },
                { icon: FileText, color: COLORS[2], label: 'Payroll Processing' },
                { icon: BarChart3, color: COLORS[3], label: 'Analytics' },
                { icon: Shield, color: COLORS[4], label: 'Compliance' },
                { icon: Zap, color: COLORS[5], label: 'Automation' },
              ].map((item, index) => (
                <div key={index} className="group">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 shadow-lg"
                    style={{ backgroundColor: `${item.color}20` }}
                  >
                    <item.icon className="w-8 h-8" style={{ color: item.color }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Call to action */}
            <div className="flex items-center justify-center gap-4">
              <div
                className="px-8 py-4 rounded-2xl font-semibold text-white text-xl shadow-lg"
                style={{ backgroundColor: COLORS[0] }}
              >
                Get Started Today
              </div>
              <div
                className="px-8 py-4 rounded-2xl font-semibold border-2 text-xl"
                style={{
                  borderColor: COLORS[1],
                  color: COLORS[1],
                  backgroundColor: 'transparent',
                }}
              >
                Learn More
              </div>
            </div>
          </div>

          {/* Subtle border glow */}
          <div
            className="absolute inset-0 rounded-2xl border-2 opacity-20"
            style={{ borderColor: COLORS[1] }}
          />
        </div>

        {/* Instructions */}
        <div className="mt-8 text-center text-gray-600 dark:text-gray-400">
          <p className="text-lg">
            This OG card is designed with proper 1200x630 dimensions for social media sharing.
          </p>
          <p className="text-sm mt-2">
            Take a screenshot of the card above to use on your landing page or social media.
          </p>
        </div>
      </div>
    </div>
  )
}
